/**
 * mockOUs.ts — Single source of truth for Organizational Unit data.
 *
 * When the API is ready, replace the exports below with real API calls.
 * All screens consume this file — changing it here propagates everywhere.
 */

import type { OuCatalogCacheStatusDto } from '@/api/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OUNode {
  key: string;
  label: string;
  /** ISO language code used as default for this OU */
  lang?: string;
  /** 'OU' | 'BU' — top-level classification */
  type?: 'OU' | 'BU';
  /** Whether this OU has special GDPR/privacy restrictions */
  gdpr?: boolean;
  /** Whether this OU is active in the rules engine */
  active?: boolean;
  /** Azure AD security group ID linked to this OU (used to resolve the Graph-based OU tree) */
  azureAdGroupId?: string;
  children?: OUNode[];
}

// ─── Master flat catalog ──────────────────────────────────────────────────────

/**
 * All OUs available in the system.
 * `key`   → lowercase slug used as CSS modifier, API param, etc.
 * `label` → display name shown in the UI.
 * `active`→ whether this OU participates in automated rules (RN-000 toggle).
 * `gdpr`  → whether special Europa/GDPR handling is required.
 */
export const OU_CATALOG: OUNode[] = [
  { key: 'mexico', label: 'México',      lang: 'es', type: 'OU', active: true  },
  { key: 'latam',  label: 'LATAM',       lang: 'pt', type: 'OU', active: true  },
  { key: 'usa',    label: 'USA',         lang: 'en', type: 'OU', active: true  },
  { key: 'fose',   label: 'FOSE',        lang: 'fr', type: 'BU', active: true  },
  { key: 'growth', label: 'GROWTH',      lang: 'en', type: 'OU', active: false },
  { key: 'europa', label: 'Europa (BU)', lang: 'de', type: 'BU', active: false, gdpr: true },
];

/** OUs that are active in the rules engine (RN-000, RN-001...). */
export const ACTIVE_OUS: OUNode[] = OU_CATALOG.filter(ou => ou.active);

/** All OUs including inactive ones (used in admin dropdowns, user management). */
export const ALL_OUS: OUNode[] = OU_CATALOG;

// ─── Display-label helpers ────────────────────────────────────────────────────

/**
 * Returns the short display label for an OU key.
 * Falls back to the key itself if not found.
 */
export function getOULabel(key: string): string {
  return OU_CATALOG.find(ou => ou.key === key)?.label ?? key;
}

/**
 * Returns the OU node for a given key, or undefined if not found.
 */
export function getOU(key: string): OUNode | undefined {
  return OU_CATALOG.find(ou => ou.key === key);
}

// ─── CSS color tokens per OU ──────────────────────────────────────────────────

/** Paleta oficial de marca (código de color CP-1 a CP-7), en orden. */
export const OU_PALETTE: string[] = [
  '#F08521', // CP-1
  '#342F82', // CP-2
  '#E11F26', // CP-3
  '#20B15A', // CP-4
  '#EF5395', // CP-5
  '#40B8E9', // CP-6
  '#D8D8D8', // CP-7
];

/** Asignación fija para las OUs conocidas del demo/mock, tomada de la paleta de marca. */
export const OU_COLORS: Record<string, string> = {
  mexico: OU_PALETTE[0]!, // CP-1
  growth: OU_PALETTE[1]!, // CP-2
  fose:   OU_PALETTE[2]!, // CP-3
  latam:  OU_PALETTE[3]!, // CP-4
  usa:    OU_PALETTE[5]!, // CP-6
  europa: OU_PALETTE[6]!, // CP-7
};

/**
 * Hash simple y determinístico (mismo id → mismo índice siempre) para asignar
 * color de la paleta a OUs reales que no están en OU_COLORS — el catálogo real
 * trae ids/nombres arbitrarios que no calzan con las claves del mock, y antes
 * todas caían al mismo gris de fallback (se veían "todas iguales").
 */
function hashOuKeyToIndex(key: string, paletteLength: number): number {
  let hash = 0;
  for (let charIndex = 0; charIndex < key.length; charIndex++) {
    hash = (hash * 31 + key.charCodeAt(charIndex)) >>> 0;
  }
  return hash % paletteLength;
}

export function getOUColor(key: string): string {
  return OU_COLORS[key] ?? OU_PALETTE[hashOuKeyToIndex(key, OU_PALETTE.length)]!;
}

// ─── Full hierarchical tree (Región → Dirección Organizacional → Dirección Funcional → Departamento) ──
// Used by SurveyBuilderView audience selector.

export const OU_TREE: OUNode[] = [
  { key: 'mexico', label: 'México', lang: 'es', type: 'OU', children: [
    { key: 'mx-cdmx', label: 'Ciudad de México', children: [
      { key: 'mx-cdmx-carnes', label: 'BU Carnes', children: [
        { key: 'mx-cdmx-carnes-com', label: 'Depto. Comercial' },
        { key: 'mx-cdmx-carnes-ops', label: 'Depto. Operaciones' },
      ]},
      { key: 'mx-cdmx-lacteos', label: 'BU Lácteos', children: [
        { key: 'mx-cdmx-lacteos-com', label: 'Depto. Comercial' },
        { key: 'mx-cdmx-lacteos-sc',  label: 'Depto. Supply Chain' },
      ]},
    ]},
    { key: 'mx-mty', label: 'Monterrey', children: [
      { key: 'mx-mty-carnes', label: 'BU Carnes', children: [
        { key: 'mx-mty-carnes-com', label: 'Depto. Comercial' },
        { key: 'mx-mty-carnes-mfg', label: 'Depto. Manufactura' },
      ]},
    ]},
    { key: 'mx-gdl', label: 'Guadalajara', children: [
      { key: 'mx-gdl-quesos', label: 'BU Quesos', children: [
        { key: 'mx-gdl-quesos-com', label: 'Depto. Comercial' },
      ]},
    ]},
  ]},
  { key: 'latam', label: 'LATAM', lang: 'pt', type: 'OU', children: [
    { key: 'la-nic', label: 'Nicaragua', children: [
      { key: 'la-nic-bu1', label: 'BU Centroamérica Norte', children: [
        { key: 'la-nic-bu1-com', label: 'Depto. Comercial' },
      ]},
    ]},
    { key: 'la-per', label: 'Perú', children: [
      { key: 'la-per-bu1', label: 'BU Perú', children: [
        { key: 'la-per-bu1-com', label: 'Depto. Comercial' },
      ]},
    ]},
  ]},
  { key: 'usa', label: 'USA', lang: 'en', type: 'OU', children: [
    { key: 'usa-sw', label: 'Southwest', children: [
      { key: 'usa-sw-bu1', label: 'BU Texas / California', children: [
        { key: 'usa-sw-bu1-com', label: 'Sales Management' },
      ]},
    ]},
  ]},
  { key: 'fose',   label: 'FOSE (BU)', lang: 'fr', type: 'BU' },
  { key: 'growth', label: 'GROWTH',    lang: 'en', type: 'OU', children: [
    { key: 'gr-digital', label: 'Digital & Datos', children: [
      { key: 'gr-digital-bu1', label: 'BU Digital', children: [
        { key: 'gr-digital-bu1-prod', label: 'Depto. Producto Digital' },
      ]},
    ]},
  ]},
];

// ─── Mock cache status for the OU-catalog cache diagnostic ──────────────────────
// Mirrors GET /v1/organizational-units/cache-status under USE_MOCK. Reports the SAME
// OU catalog cache (catalog:ous:{tenantId}) that powers the OU dropdown (ACTIVE_OUS
// above), not a separate Graph "forest" cache (that flow was removed once OUs began
// syncing into Mongo automatically).
export const MOCK_OU_CATALOG_CACHE_STATUS: OuCatalogCacheStatusDto = {
  isPopulated: true,
  ouCount: ACTIVE_OUS.length,
  lastSyncedAt: new Date().toISOString(),
};

// ─── Tree utilities (used by SurveyBuilderView) ───────────────────────────────

export function findNode(key: string, nodes: OUNode[]): OUNode | null {
  for (const n of nodes) {
    if (n.key === key) return n;
    if (n.children) {
      const found = findNode(key, n.children);
      if (found) return found;
    }
  }
  return null;
}
